import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { HeroBackgroundMesh } from "@/components/HeroBackgroundMesh";
import { HeroBackgroundSplit } from "@/components/HeroBackgroundSplit";
import { HeroBackgroundStudio } from "@/components/HeroBackgroundStudio";
import { HERO_BG_VARIANT } from "@/constants/heroVariant";
import { scrollToId } from "@/lib/scroll";

const LINES = [
    { text: "Your Wish.", cls: "text-brand-deep" },
    { text: "Your Space.", cls: "text-brand-deep" },
    { text: "Made Real.", cls: "italic text-brand-teal" },
];

export const Hero = () => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const contentY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.2]);

    const bgByVariant = {
        studio: <HeroBackgroundStudio />,
        mesh: <HeroBackgroundMesh />,
        split: <HeroBackgroundSplit />,
    };

    const sectionBg =
        HERO_BG_VARIANT === "split"
            ? "bg-white"
            : HERO_BG_VARIANT === "mesh"
              ? "bg-white"
              : "bg-gradient-to-br from-white via-brand-surface/50 to-brand-mist/70";

    return (
        <section
            id="hero"
            ref={ref}
            data-testid="hero-section"
            data-hero-variant={HERO_BG_VARIANT}
            className={`relative overflow-hidden ${sectionBg}`}
        >
            {bgByVariant[HERO_BG_VARIANT] ?? <HeroBackgroundStudio />}

            <div className="relative mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-center gap-10 px-5 pb-28 pt-36 sm:px-8 lg:grid-cols-12 lg:pb-20 lg:pt-24">
                <motion.div style={{ y: contentY, opacity: contentOpacity }} className="lg:col-span-7">
                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.05 }}
                        data-testid="hero-badge"
                        className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-brand-teal/15 bg-brand-surface px-4 py-2"
                    >
                        <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
                        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-teal">
                            Renovation Made Simple
                        </span>
                    </motion.div>

                    <h1 className="font-display text-5xl font-semibold leading-[0.98] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="hero-headline">
                        {LINES.map((line, i) => (
                            <span key={line.text} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${line.cls}`}
                                    initial={reduce ? false : { y: "112%" }}
                                    animate={{ y: "0%" }}
                                    transition={{ duration: 0.95, delay: 0.18 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={reduce ? false : { opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.65 }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-brand-muted sm:text-lg"
                        data-testid="hero-subtext"
                    >
                        From design to renovation, Reno Genie brings everything together — the right services, the right
                        professionals, the right plan — to transform your space.
                    </motion.p>

                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.8 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            type="button"
                            onClick={() => scrollToId("#cta")}
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center gap-2.5 rounded-full bg-brand-gold px-7 py-4 text-sm font-bold text-brand-deep transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(253,185,39,0.42)]"
                        >
                            Get Started
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            type="button"
                            onClick={() => scrollToId("#services")}
                            data-testid="hero-cta-secondary"
                            className="inline-flex items-center gap-2.5 rounded-full border border-brand-teal/25 px-7 py-4 text-sm font-semibold text-brand-teal transition-all duration-300 hover:-translate-y-1 hover:border-brand-teal hover:bg-brand-teal hover:text-white"
                        >
                            Explore Services
                        </button>
                    </motion.div>
                </motion.div>

                <div className="relative hidden min-h-[420px] lg:col-span-5 lg:block">
                    <div className="genie-static absolute right-0 top-1/2 w-[78%] -translate-y-1/2">
                        <img
                            src="/brand/genie.png"
                            alt="Reno Genie mascot"
                            data-testid="hero-genie-static"
                            className="genie-inner w-full"
                        />
                    </div>
                </div>
            </div>

            <div className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 lg:flex" data-testid="scroll-indicator">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-muted">Scroll</span>
                <div className="scroll-line" />
            </div>
        </section>
    );
};
